import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Filter, Building2, Home as HomeIcon, UtensilsCrossed } from 'lucide-react';
import { Button } from '../components/ui/button';
import { projects } from '../data/mock';

const Projects = () => {
  const [activeFilter, setActiveFilter] = useState('All');

  const categories = ['All', 'Hospitality', 'Commercial', 'Residential'];

  const filteredProjects = activeFilter === 'All'
    ? projects
    : projects.filter(project => project.category === activeFilter);

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Hospitality':
        return UtensilsCrossed;
      case 'Commercial':
        return Building2;
      case 'Residential':
        return HomeIcon;
      default:
        return Building2;
    }
  };

  const getCategoryCount = (category) => {
    if (category === 'All') return projects.length;
    return projects.filter(p => p.category === category).length;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-28 pb-12 bg-neutral-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-red-500 text-sm font-semibold tracking-widest uppercase">
              Our Portfolio
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mt-2 mb-4">
              Projects That
              <span className="block text-red-500">Define Excellence</span>
            </h1>
            <p className="text-lg text-neutral-300 leading-relaxed">
              Explore our portfolio of completed projects spanning hospitality,
              commercial, and residential construction.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-4 bg-white sticky top-[72px] z-30 border-b border-neutral-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-neutral-500">
              <Filter className="w-4 h-4" />
              <span className="text-sm font-medium">Filter:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveFilter(category)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-300 ${
                    activeFilter === category
                      ? 'bg-red-600 text-white'
                      : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                  }`}
                >
                  {category} ({getCategoryCount(category)})
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-8 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {filteredProjects.map((project) => {
              const CategoryIcon = getCategoryIcon(project.category);
              return (
                <div
                  key={project.id}
                  className="group bg-white rounded-xl overflow-hidden border border-neutral-200 hover:border-red-500/50 transition-all duration-300 hover:-translate-y-1 shadow-sm"
                >
                  <div className="relative aspect-square overflow-hidden bg-neutral-900 flex items-center justify-center p-4">
                    <img
                      src={project.image}
                      alt={project.name}
                      className="max-w-full max-h-full object-contain transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute top-2 left-2">
                      <span className="inline-flex items-center gap-1 bg-white/90 backdrop-blur-sm text-red-600 text-xs font-medium px-2 py-1 rounded-full">
                        <CategoryIcon className="w-3 h-3" />
                        {project.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="text-base font-semibold text-neutral-900 mb-1 group-hover:text-red-600 transition-colors line-clamp-1">
                      {project.name}
                    </h3>
                    <p className="text-neutral-500 text-xs mb-2 line-clamp-1">
                      {project.description}
                    </p>
                    <span className="text-red-600 font-medium text-xs">
                      {project.size}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-10 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-neutral-50 rounded-xl p-6 border border-neutral-200 text-center">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-3 mx-auto">
                <UtensilsCrossed className="w-6 h-6 text-red-600" />
              </div>
              <div className="text-3xl font-bold text-red-600 mb-1">105</div>
              <h3 className="text-neutral-900 font-semibold text-sm mb-1">Hospitality Venues</h3>
              <p className="text-neutral-500 text-xs">Restaurants, bars, nightclubs</p>
            </div>
            <div className="bg-neutral-50 rounded-xl p-6 border border-neutral-200 text-center">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-3 mx-auto">
                <Building2 className="w-6 h-6 text-red-600" />
              </div>
              <div className="text-3xl font-bold text-red-600 mb-1">100+</div>
              <h3 className="text-neutral-900 font-semibold text-sm mb-1">Commercial Projects</h3>
              <p className="text-neutral-500 text-xs">Offices, medical, educational</p>
            </div>
            <div className="bg-neutral-50 rounded-xl p-6 border border-neutral-200 text-center">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-3 mx-auto">
                <HomeIcon className="w-6 h-6 text-red-600" />
              </div>
              <div className="text-3xl font-bold text-red-600 mb-1">200+</div>
              <h3 className="text-neutral-900 font-semibold text-sm mb-1">Residential Units</h3>
              <p className="text-neutral-500 text-xs">Custom homes, subdivisions</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-10 bg-red-600">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Have a Project in Mind?
          </h2>
          <p className="text-red-100 max-w-2xl mx-auto mb-6">
            Let's discuss how we can add your project to our portfolio of
            successful builds.
          </p>
          <Link to="/contact">
            <Button className="bg-white hover:bg-neutral-100 text-red-600 font-semibold px-6 py-5">
              Start Your Project
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Projects;
